import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Badge, Button, Grid, Space, Tooltip, Typography } from 'antd';
import {
  BellOutlined,
  MenuOutlined,
  MinusOutlined,
  PlusOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import alarmSoundService from '../../services/alarmSoundService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../../stores/authStore';
import useAlarmStore from '../../stores/alarmStore';
import useSystemStore, { ZOOM_MIN, ZOOM_MAX } from '../../stores/systemStore';
import useDeviceStore from '../../stores/deviceStore';
import useAuth from '../../hooks/useAuth';
import useDeviceCatalogStore from '../../stores/deviceCatalogStore';
import { formatTimeAgo } from '../../utils/formatters';
import { PAGE_TABS } from '../../utils/tabRegistry';
import type { TabDef } from '../../utils/tabRegistry';
import { hasPageAccess, hasTabAccess } from '../../constants/permissions';
import { SYSTEM_VERSION } from '../../constants/appVersion';

const { Text } = Typography;

interface AppHeaderProps {
  isMobile?: boolean;
  onMobileMenuOpen?: () => void;
}

export default function AppHeader({ isMobile, onMobileMenuOpen }: AppHeaderProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const alarmRoleFilter = useAuthStore((s) => s.alarmRoleFilter);
  const stats = useAlarmStore((s) => s.stats);
  const activeAlarms = useAlarmStore((s) => s.activeAlarms);
  const wsConnected = useSystemStore((s) => s.wsConnected);
  const modbusConnected = useSystemStore((s) => s.modbusConnected);
  const mqttConnected = useSystemStore((s) => s.mqttConnected);
  const modbusSnapshot = useDeviceStore((s) => s.modbusSnapshot);
  const activeTabMap = useSystemStore((s) => s.activeTabMap);
  const setActiveTab = useSystemStore((s) => s.setActiveTab);
  const pagePermissions = useAuthStore((s) => s.pagePermissions);
  const branding = useSystemStore((s) => s.branding);
  const zoomLevel = useSystemStore((s) => s.zoomLevel);
  const zoomIn = useSystemStore((s) => s.zoomIn);
  const zoomOut = useSystemStore((s) => s.zoomOut);
  const resetZoom = useSystemStore((s) => s.resetZoom);

  const screens = Grid.useBreakpoint();
  const isWide = !!screens.lg;   // ≥ 992px：顯示縮放控制
  const isXWide = !!screens.xl;  // ≥ 1200px：顯示頁面副標題（防止 130% 縮放時擠壓）

  // 未確認警報數（套用角色過濾）
  //
  // ⚠️ 2026-10-23：這裡原本是五處中**最嚴**的一處，錯了兩層：
  //    ① `filter[userRole] === true` 把「沒填」當成不接收
  //    ② 矩陣完全沒列到的類型也排除（`return !filterConfigured`），
  //       而後端 role_excluded_types 對未登錄的類型是 default-allow
  //    ⇒ 鈴鐺的未確認數會比警報列表還少，兩個畫面互相矛盾。
  //    統一為「只有明確 false 才排除」，未列到的類型與沒填的角色都算可見。
  //    `?.` 讓「類型不在矩陣中」自然落到可見，不需要額外分支。
  const unackCount = useMemo(() => {
    if (!user) return 0;
    // dev 使用伺服器計數（避免 activeAlarms 100 筆截斷影響）；admin 依角色過濾
    if (user.role_level <= 1) return stats?.total_unacknowledged || 0;
    const userRole = user.role;
    return activeAlarms.filter((alarm) => {
      const filter = alarmRoleFilter[alarm.alarm_type];
      return filter?.[userRole] !== false;
    }).length;
  }, [activeAlarms, alarmRoleFilter, user, stats]);

  // 判斷使用者是否能進入 /alarm 頁面（頁面權限 + 通知權限覆寫）
  const canAccessAlarm = useMemo(() => {
    if (!user) return false;
    return hasPageAccess('/alarm', user.role_level, user.role, pagePermissions, alarmRoleFilter);
  }, [user, pagePermissions, alarmRoleFilter]);

  const isDashboard = location.pathname === '/';
  const { roleLevel } = useAuth();

  // 警報聲音靜音控制
  const [soundMuted, setSoundMuted] = useState(() => alarmSoundService.isMuted());
  const toggleSoundMute = useCallback(() => {
    const next = alarmSoundService.toggleMute();
    setSoundMuted(next);
  }, []);

  // 全螢幕狀態
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((e) => console.warn('[Header] Fullscreen request failed:', e));
    } else {
      document.exitFullscreen().catch((e) => console.warn('[Header] Exit fullscreen failed:', e));
    }
  };

  // 動態監控 Tab：從 deviceCatalogStore 取得（若已載入），否則 fallback 到靜態 PAGE_TABS
  const catalogTabs = useDeviceCatalogStore((s) => s.tabs);
  const catalogLoaded = useDeviceCatalogStore((s) => s.loaded);
  const getTabDeviceKeysMap = useDeviceCatalogStore((s) => s.getTabDeviceKeysMap);

  const pageTabConfig = useMemo(() => {
    if (location.pathname === '/monitoring' && catalogLoaded && catalogTabs.length > 0) {
      const deviceTabs = catalogTabs.map(ct => ({ key: ct.key, labelKey: ct.label } as TabDef));
      // 將 PAGE_TABS 中非設備型 Tab（如 schedule）附加到尾端
      const staticDef = PAGE_TABS['/monitoring'];
      if (staticDef) {
        const catalogKeys = new Set(deviceTabs.map(t => t.key));
        const extraTabs = staticDef.tabs.filter(t => !catalogKeys.has(t.key));
        deviceTabs.push(...extraTabs);
      }
      return {
        defaultTab: deviceTabs[0].key,
        tabs: deviceTabs,
      };
    }
    // /monitoring 完全由設備 catalog 決定；catalog 未就緒前**不要**退回美濃靜態 tab，
    // 否則馬修精靈設定會把靜態 tab（cold/hitachi/iot627/aibox）過濾到只剩共通的 aibox，
    // 造成「分頁列只出現 AI控箱、要按刷新才補齊」。改為空 tabs，待 catalog 載入自動補齊。
    if (location.pathname === '/monitoring') {
      return { defaultTab: 'brine', tabs: [] };
    }
    return PAGE_TABS[location.pathname];
  }, [location.pathname, catalogLoaded, catalogTabs]);

  // 支援 ?tab=xxx query param（用於 /manual → /system?tab=manual 等重導向）
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && pageTabConfig) {
      const valid = pageTabConfig.tabs.some(t => t.key === tabParam);
      if (valid && activeTabMap[location.pathname] !== tabParam) {
        setActiveTab(location.pathname, tabParam);
      }
    }
  }, [location.search, location.pathname, pageTabConfig, activeTabMap, setActiveTab]);

  const activeTab = pageTabConfig
    ? (activeTabMap[location.pathname] ?? pageTabConfig.defaultTab)
    : '';

  // 連線狀態圓點（Tooltip hover 顯示名稱）
  const statusDot = (connected: boolean, label: string) => (
    <Tooltip title={`${label}: ${connected ? '已連線' : '已斷線'}`}>
      <span
        style={{
          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
          background: connected ? '#52c41a' : '#ff4d4f',
          boxShadow: `0 0 4px ${connected ? '#52c41a' : '#ff4d4f'}`,
          cursor: 'default',
        }}
      />
    </Tooltip>
  );

  const visibleTabs = pageTabConfig
    ? pageTabConfig.tabs.filter((tab) => {
        // Tab 權限檢查：compound key in pagePermissions → fallback to requiredLevel
        if (!hasTabAccess(location.pathname, tab.key, roleLevel, user?.role ?? '', pagePermissions)) {
          return false;
        }
        return true;
      })
    : [];

  // 如果目前選取的 tab 因權限限制而隱藏，自動切換至第一個可見 tab
  useEffect(() => {
    if (!pageTabConfig) return;
    // /monitoring 的 tab 由設備 catalog 決定；catalog 載入前會先用靜態（美濃）tab，
    // 此時精靈設定（馬修）會把靜態 tab 過濾到只剩共通的 aibox，導致誤切到 AI 控箱。
    // 故 catalog 尚未載入前不自動切換，等真正的 tab 清單就緒再判斷。
    if (location.pathname === '/monitoring' && !catalogLoaded) return;
    const current = activeTabMap[location.pathname] ?? pageTabConfig.defaultTab;
    if (visibleTabs.length > 0) {
      const isVisible = visibleTabs.some(t => t.key === current);
      if (!isVisible) {
        setActiveTab(location.pathname, visibleTabs[0].key);
      }
    } else if (current !== pageTabConfig.defaultTab) {
      // 所有 tab 皆被過濾時，回到預設 tab（避免空白頁）
      setActiveTab(location.pathname, pageTabConfig.defaultTab);
    }
  }, [visibleTabs.length, activeTabMap, location.pathname, pageTabConfig, setActiveTab, catalogLoaded]);

  // 分頁多時，選中的 tab 自動捲入捲動視野中央 → 當前分頁永不被截在邊緣（配合 tab 列 overflowX:auto）
  const activeTabElRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    activeTabElRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [activeTab, visibleTabs.length]);

  // 縮放控制元件（≥ 992px 顯示）— 使用 Ant Design Button 確保點擊可靠
  const zoomControls = isWide && (
    <Space size={0} style={{
      background: 'var(--sw-bg)', borderRadius: 7,
      border: '1px solid var(--sw-border)', height: 28, display: 'inline-flex', alignItems: 'center',
    }}>
      <Button
        type="text"
        size="small"
        icon={<MinusOutlined style={{ fontSize: 10 }} />}
        disabled={zoomLevel <= ZOOM_MIN}
        onClick={() => zoomOut()}
        style={{ width: 26, height: 28, borderRadius: 0, borderRight: '1px solid var(--sw-border)' }}
      />
      <span
        onClick={() => resetZoom()}
        style={{
          display: 'inline-block', width: 40, textAlign: 'center', fontSize: 11, fontWeight: 600,
          color: zoomLevel === 1.0 ? 'var(--sw-text-secondary)' : 'var(--sw-primary)',
          cursor: 'pointer', userSelect: 'none', lineHeight: '28px',
        }}
      >
        {Math.round(zoomLevel * 100)}%
      </span>
      <Button
        type="text"
        size="small"
        icon={<PlusOutlined style={{ fontSize: 10 }} />}
        disabled={zoomLevel >= ZOOM_MAX}
        onClick={() => zoomIn()}
        style={{ width: 26, height: 28, borderRadius: 0, borderLeft: '1px solid var(--sw-border)', borderRight: '1px solid var(--sw-border)' }}
      />
      <Button
        type="text"
        size="small"
        icon={isFullscreen ? <FullscreenExitOutlined style={{ fontSize: 13 }} /> : <FullscreenOutlined style={{ fontSize: 13 }} />}
        onClick={toggleFullscreen}
        style={{ width: 28, height: 28, borderRadius: 0 }}
      />
    </Space>
  );

  // 監控頁分頁多（7+），桌面改用「主列下方整行 sub-bar」吃滿整個視窗寬度，避免與品牌/控制鈕壓線
  const useDesktopTabBar = !isMobile && location.pathname === '/monitoring';

  // 主 Header bar（高度 64px）
  const mainBar = (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 12px', height: 64,
    }}>
      {/* 左側：漢堡 + 品牌 + Dashboard 標題 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16, flex: 1, minWidth: 0 }}>
        {/* 手機漢堡按鈕 */}
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 18 }} />}
            onClick={onMobileMenuOpen}
            style={{ padding: '4px 8px', flexShrink: 0 }}
          />
        )}

        {/* 品牌名稱 — 高縮放時允許截斷，讓 Tab 有足夠空間 */}
        <Text strong className="header-brand-text" style={{
          color: 'var(--sw-text)', whiteSpace: 'nowrap',
          flexShrink: zoomLevel > 1.2 ? 1 : 0,
          overflow: 'hidden', textOverflow: 'ellipsis',
          minWidth: zoomLevel > 1.2 ? 80 : 'auto',
        }}>
          {branding.name}
        </Text>

        {/* Dashboard 標題（≥ 1200px 才顯示，防 130% 縮放時擠壓） */}
        {isDashboard && isXWide && (
          <Space size="middle" style={{ flexWrap: 'nowrap' }}>
            <span style={{
              fontSize: 14, color: 'var(--sw-primary)',
              borderBottom: '2px solid var(--sw-primary)', paddingBottom: 4,
              whiteSpace: 'nowrap',
            }}>
              冷藏系統總覽
            </span>
            {modbusSnapshot?.timestamp && (
              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                更新: {formatTimeAgo(modbusSnapshot.timestamp)}
              </Text>
            )}
          </Space>
        )}

        {/* 頁面 Tab 導覽（桌面在 Header 顯示，手機/監控頁移到獨立整行 sub-bar） */}
        {pageTabConfig && !isMobile && !useDesktopTabBar && (
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: zoomLevel > 1.3 ? 6 : zoomLevel > 1.1 ? 8 : 12,
            flexWrap: 'nowrap', overflowX: 'auto',
            flex: 1, minWidth: 0,
            scrollbarWidth: 'none', paddingRight: 8,
          }}>
            {visibleTabs.map((tab) => {
              const active = activeTab === tab.key;
              return (
                <span
                  key={tab.key}
                  ref={active ? activeTabElRef : undefined}
                  onClick={() => {
                    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
                    setActiveTab(location.pathname, tab.key);
                  }}
                  style={{
                    cursor: 'pointer', flex: 'none',
                    fontSize: zoomLevel > 1.3 ? 11 : zoomLevel > 1.2 ? 12 : 14,
                    whiteSpace: 'nowrap',
                    color: active ? 'var(--sw-primary)' : 'var(--sw-text-muted)',
                    borderBottom: active ? '2px solid var(--sw-primary)' : '2px solid transparent',
                    paddingBottom: 4, transition: 'color 0.2s, border-color 0.2s',
                  }}
                >
                  {t(tab.labelKey)}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* 右側：連線狀態 + 縮放控制 + 警報 + 使用者 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, flexShrink: 0 }}>
        {/* 版號 V1.xx.xx（2026-10-15 比照美濃監控站；來源 constants/appVersion.ts）；手機隱藏避免擠壓。
            2026-10-16 Frank：顏色由 --sw-text 調淡為 --sw-text-secondary。用 token 而非寫死灰色 ——
            三套主題（橘暖 #8B8580／VIS #6B7280／深色 #9CA3AF）都有定義，深色模式下會自動變亮。 */}
        {!isMobile && (
          <b style={{ fontFamily: 'Consolas, "Courier New", monospace', color: 'var(--sw-text-secondary)', fontSize: 12, whiteSpace: 'nowrap' }}>
            {SYSTEM_VERSION}
          </b>
        )}
        {/* 連線狀態：圓點 + hover 顯示名稱 */}
        <Space size={6}>
          {statusDot(modbusConnected, 'Modbus')}
          {statusDot(mqttConnected, 'MQTT')}
          {statusDot(wsConnected, 'WebSocket')}
        </Space>

        {/* 縮放控制（桌面，在連線狀態與小叮噹之間） */}
        {zoomControls}

        {/* 警報聲音靜音切換（僅 dev 可見） */}
        {roleLevel <= 1 && (
          <Tooltip title={soundMuted ? '啟用警報音效' : '關閉警報音效'}>
            <SoundOutlined
              onClick={toggleSoundMute}
              style={{
                fontSize: 18,
                color: soundMuted ? '#BFBFBF' : 'var(--sw-text)',
                cursor: 'pointer',
                textDecoration: soundMuted ? 'line-through' : 'none',
                transition: 'color 0.2s',
              }}
            />
          </Tooltip>
        )}

        {/* 警報 Badge（offset 往內拉，避免數字徽章凸出視窗右緣造成手機橫向捲軸） */}
        <Tooltip title={!canAccessAlarm ? '無警報頁面存取權限' : undefined}>
          <Badge count={unackCount} overflowCount={99} offset={[-8, 2]}>
            <BellOutlined
              style={{ fontSize: 20, color: canAccessAlarm ? 'var(--sw-text)' : '#BFBFBF', cursor: canAccessAlarm ? 'pointer' : 'not-allowed' }}
              onClick={() => canAccessAlarm && navigate('/alarm')}
            />
          </Badge>
        </Tooltip>
      </div>
    </div>
  );

  // 手機 sub-bar：Tab 導覽
  const mobileTabBar = isMobile && visibleTabs.length > 0 ? (
    <div style={{
      height: 40, borderTop: '1px solid var(--sw-border)',
      overflowX: 'auto', display: 'flex', alignItems: 'center',
      padding: '0 12px', gap: 20, background: 'var(--sw-card)',
      scrollbarWidth: 'none',
    }}>
      {visibleTabs.map((tab) => {
        const active = activeTab === tab.key;
        return (
          <span
            key={tab.key}
            ref={active ? activeTabElRef : undefined}
            onClick={() => {
              if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
              setActiveTab(location.pathname, tab.key);
            }}
            style={{
              cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
              color: active ? 'var(--sw-primary)' : 'var(--sw-text-muted)',
              borderBottom: active ? '2px solid var(--sw-primary)' : '2px solid transparent',
              paddingBottom: 4, transition: 'color 0.2s, border-color 0.2s',
              flexShrink: 0,
            }}
          >
            {t(tab.labelKey)}
          </span>
        );
      })}
    </div>
  ) : null;

  // 桌面監控頁 sub-bar：分頁獨立整行，吃滿整個視窗寬度（分頁多也不壓線）
  const desktopTabBar = useDesktopTabBar && pageTabConfig && visibleTabs.length > 0 ? (
    <div style={{
      height: 44, borderTop: '1px solid var(--sw-border)',
      overflowX: 'auto', display: 'flex', alignItems: 'center',
      padding: '0 16px', gap: 24, background: 'var(--sw-card)',
      scrollbarWidth: 'none',
    }}>
      {visibleTabs.map((tab) => {
        const active = activeTab === tab.key;
        return (
          <span
            key={tab.key}
            ref={active ? activeTabElRef : undefined}
            onClick={() => {
              if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
              setActiveTab(location.pathname, tab.key);
            }}
            style={{
              cursor: 'pointer', fontSize: 15, whiteSpace: 'nowrap', flexShrink: 0,
              fontWeight: active ? 600 : 400,
              color: active ? 'var(--sw-primary)' : 'var(--sw-text-muted)',
              borderBottom: active ? '2px solid var(--sw-primary)' : '2px solid transparent',
              paddingBottom: 4, transition: 'color 0.2s, border-color 0.2s',
            }}
          >
            {t(tab.labelKey)}
          </span>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      {mainBar}
      {desktopTabBar}
      {mobileTabBar}
    </>
  );
}
